/**
 * Jain Gaurav Yatra — City Info CRUD API (the "Discover Ahmedabad" content).
 * Backend: /jain_gaurav_yatra/city-info/  (cookie-session auth + X-Tenant-ID).
 * JSON body (no file uploads), paginated list response.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

/** Tenant + optional Bearer. JSON endpoints, so Content-Type is set explicitly. */
function authHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const CITY_PATH = "/jain_gaurav_yatra/city-info/";

/** A label/value pair in the quick-facts strip. */
export interface QuickFact {
  label: string;
  value: string;
}

/** A titled prose block, rendered as one accordion entry. */
export interface CitySection {
  title: string;
  content: string;
}

/** Page-facing city-info shape. */
export interface CityInfo {
  id: string;
  name: string;
  subtitle: string;
  quick_facts: QuickFact[];
  sections: CitySection[];
  is_active: boolean;
  created_at: string;
}

/** Fields accepted when creating/updating. */
export interface CityInfoWrite {
  name: string;
  subtitle: string;
  quick_facts: QuickFact[];
  sections: CitySection[];
  is_active: boolean;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

function toQuickFacts(v: unknown): QuickFact[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v
    .map((row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      return { label: str(r.label), value: str(r.value) };
    })
    .filter((f) => f.label || f.value);
}

function toSections(v: unknown): CitySection[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v
    .map((row) => {
      const r = (row ?? {}) as Record<string, unknown>;
      return { title: str(r.title), content: str(r.content) };
    })
    .filter((s) => s.title || s.content);
}

function toCityInfo(row: Record<string, unknown>): CityInfo {
  return {
    id: str(row.id),
    name: str(row.name),
    subtitle: str(row.subtitle),
    quick_facts: toQuickFacts(row.quick_facts),
    sections: toSections(row.sections),
    is_active: row.is_active !== false,
    created_at: str(row.created_at),
  };
}

function toBody(p: CityInfoWrite): string {
  return JSON.stringify({
    name: p.name,
    subtitle: p.subtitle,
    quick_facts: p.quick_facts,
    sections: p.sections,
    is_active: p.is_active,
  });
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

/** Unwrap either a bare array or a `{ results: [...] }` page. */
function rowsOf(data: unknown): Record<string, unknown>[] {
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return rows as Record<string, unknown>[];
}

export async function listCityInfo(): Promise<CityInfo[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${CITY_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return rowsOf(data).map(toCityInfo);
}

/**
 * The single active city record for the public homepage. Never rejects — the landing page must not
 * break if the endpoint is unreachable for an anonymous visitor; it just renders nothing.
 */
export async function getActiveCityInfo(): Promise<CityInfo | null> {
  try {
    const all = await listCityInfo();
    return all.find((c) => c.is_active) ?? all[0] ?? null;
  } catch {
    return null;
  }
}

export async function createCityInfo(payload: CityInfoWrite): Promise<CityInfo> {
  const base = apiBase();
  const res = await fetch(`${base}${CITY_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toCityInfo(data as Record<string, unknown>);
}

export async function updateCityInfo(id: string, payload: CityInfoWrite): Promise<CityInfo> {
  const base = apiBase();
  const res = await fetch(`${base}${CITY_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toCityInfo(data as Record<string, unknown>);
}

export async function deleteCityInfo(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${CITY_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
